import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

function DocumentFoldersPage() {
  const history = useHistory();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingFolderUuid, setDeletingFolderUuid] = useState("");

  useEffect(() => {
    fetchPageData();
  }, []);

  async function handleFolderDelete(folderUuid) {
    setDeletingFolderUuid(folderUuid);

    try {
      await api.delete(`/document-folders/${folderUuid}`);
      toast.success("Folder deleted");
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete folder.");
    } finally {
      setDeletingFolderUuid("");
    }
  }

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const foldersResponse = await api.get("/document-folders");
      setFolders(foldersResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load document folders.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Documents Management"
        action={
          <div className="document-page-header-action">
            <button type="button" className="document-explorer-action-button" onClick={() => history.push("/documents/new")}>
              <span className="document-explorer-action-icon">
                <i className="bi bi-folder-plus" />
              </span>
              <span>New folder</span>
            </button>
          </div>
        }
      />

      <div className="content-card table-card">
        <h5 className="mb-3">Document folders</h5>
        {loading ? <LoadingState label="Loading document folders..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {!loading && !error && !folders.length ? (
          <EmptyState title="No folders yet" text="Create the first folder to start uploading documents." />
        ) : null}

        {!loading && folders.length ? (
          <div className="table-responsive document-explorer-table-wrap">
            <table className="table align-middle mb-0 document-explorer-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="document-explorer-date-cell">Last modified</th>
                  <th className="text-end document-explorer-action-cell">Action</th>
                </tr>
              </thead>
              <tbody>
                {folders.map((folder) => (
                  <tr key={folder.uuid}>
                    <td className="document-explorer-name-cell">
                      <button
                        type="button"
                        className="document-explorer-name-button"
                        onClick={() => history.push(`/documents/${folder.uuid}`)}
                      >
                        <span className="document-explorer-folder-icon">
                          <i className="bi bi-folder-fill" />
                        </span>
                        <span className="document-explorer-name-copy">{folder.name || "-"}</span>
                      </button>
                    </td>
                    <td className="document-explorer-date-cell">{formatDate(folder.last_updated)}</td>
                    <td className="text-end document-explorer-action-cell">
                      <div className="d-inline-flex gap-2 justify-content-end flex-nowrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => history.push(`/documents/${folder.uuid}/edit`)}
                        >
                          <i className="bi bi-pencil-square me-2" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleFolderDelete(folder.uuid)}
                          disabled={deletingFolderUuid === folder.uuid}
                        >
                          {deletingFolderUuid === folder.uuid ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-trash me-2" />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default DocumentFoldersPage;
